/**
 * PresetsConfig — список пресетов с настройкой margin_multiplier.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sliders } from 'lucide-react';

import { InfoTip } from '@/components/admin/InfoTip';

import { getPresetDisplayName, usePresets, useUpdatePreset, type PresetTier } from '@/hooks/useAdmin';
import { cn } from '@/lib/utils';

const PRESET_BADGES: Record<string, string> = {
  economy: 'bg-success-tint/10 text-success border-success-tint/20',
  optimal: 'bg-primary-500/10 text-primary-400 border-primary-500/20',
  max: 'bg-warning-tint/10 text-warning border-warning-tint/20',
};

export function PresetsConfig() {
  const { t, i18n } = useTranslation();
  const { data: presets, isLoading } = usePresets();
  const update = useUpdatePreset();

  if (isLoading) {
    return <div className="text-sm text-surface-400">Loading presets…</div>;
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-surface-400">
        {t('system.presets.help')}
      </p>
      {(presets ?? []).map((p) => (
        <PresetCard
          key={p.id}
          preset={p}
          locale={i18n.language}
          onUpdate={(body) => update.mutate({ id: p.id, body })}
          updating={update.isPending && update.variables?.id === p.id}
        />
      ))}
    </div>
  );
}

function PresetCard({
  preset,
  locale,
  onUpdate,
  updating,
}: {
  preset: PresetTier;
  locale: string;
  onUpdate: (body: { margin_multiplier?: number; is_active?: boolean }) => void;
  updating: boolean;
}) {
  const { t } = useTranslation();
  const [margin, setMargin] = useState(preset.margin_multiplier);
  const [isActive, setIsActive] = useState(preset.is_active);
  const [dirty, setDirty] = useState(false);

  // Если preset пришёл с сервера обновлённый — синхронизируем
  if (
    !dirty &&
    (margin !== preset.margin_multiplier || isActive !== preset.is_active)
  ) {
    setMargin(preset.margin_multiplier);
    setIsActive(preset.is_active);
  }

  const handleSave = () => {
    const body: { margin_multiplier?: number; is_active?: boolean } = {};
    if (parseFloat(margin) !== parseFloat(preset.margin_multiplier)) {
      body.margin_multiplier = parseFloat(margin);
    }
    if (isActive !== preset.is_active) {
      body.is_active = isActive;
    }
    onUpdate(body);
    setDirty(false);
  };

  return (
    <div className="card flex items-center gap-4">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="w-10 h-10 rounded-xl bg-surface-800/60 flex items-center justify-center shrink-0">
          <Sliders className="w-5 h-5 text-surface-300" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold">{getPresetDisplayName(preset, locale)}</span>
            <span
              className={cn(
                'text-xs px-2 py-0.5 rounded-md border uppercase',
                PRESET_BADGES[preset.key] ?? 'bg-surface-700/30 text-surface-400 border-surface-700',
              )}
            >
              {preset.key}
            </span>
          </div>
          <div className="text-xs text-surface-500">margin_multiplier</div>
        </div>
      </div>

      <div className="flex items-center gap-3 shrink-0">
        <label className="flex items-center gap-2 text-xs text-surface-400">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => {
              setIsActive(e.target.checked);
              setDirty(true);
            }}
          />
          {t('system.presets.active')}
        </label>
        <input
          type="number"
          step="0.1"
          min="1"
          max="100"
          className="input w-24 text-right"
          aria-label={t('system.presets.marginMultiplier')}
          title={t('system.presets.marginMultiplier')}
          value={margin}
          onChange={(e) => {
            setMargin(e.target.value);
            setDirty(true);
          }}
        />
        <InfoTip text={t('system.presets.marginMultiplier_tip', { defaultValue: 'Наценка к курсу для расчёта стоимости для клиента' })} />
        {dirty && (
          <button onClick={handleSave} disabled={updating} className="btn-primary text-sm">
            {updating ? '…' : t('system.presets.save')}
          </button>
        )}
      </div>
    </div>
  );
}
